<template>
  <div class="race-telemetry-section" :class="`race-telemetry-section--${screenOrientationType}`">
    <img
      class="race-telemetry-section__bg"
      src="assets/img/RaceVideoPage/telemetry/telemetry_bg.png"
      alt=""
      width="343"
      height="501"
    />

    <telemetry-ots-parts
      v-model="currentTelemetryData.ots"
      :otsEnabled="currentTelemetryData.otsEnabled"
      :otsDisabledTime="currentTelemetryData.otsDisabledTime"
    />

    <telemetry-gear-pos-parts v-model="currentTelemetryData.gear" />
    <telemetry-car-speed-parts v-model="currentTelemetryData.speed" :diffMs="diffMs" />
    <telemetry-rpm-parts v-model="currentTelemetryData.rpm" :diffMs="diffMs" />
    <telemetry-steering-angle-parts v-model="currentTelemetryData.steering" :diffMs="diffMs" />

    <telemetry-brk-press-fr-parts v-model="currentTelemetryData.brake" :max="70" />
    <telemetry-rpm-gauge-parts v-model="currentTelemetryData.rpm" :max="11000" />
    <telemetry-throttle-pedal-parts v-model="currentTelemetryData.throttle" :max="100" />

    <telemetry-fuel-used-parts v-model="currentTelemetryData.fuel" :diffMs="diffMs" :max="80" />

    <telemetry-tire-temperature-section
      v-model="currentTelemetryData.tireTemperature"
      :diffMs="diffMs"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from '@vue/composition-api'
import useTelemetrySectionUtil from '@/components/RaceVideoPage/RaceVideoDataViewPane/hook/useTelemetrySectionUtil'
import TelemetryGearPosParts from '@/components/RaceVideoPage/RaceVideoDataViewPane/parts/TelemetryGearPosParts.vue'
import TelemetryCarSpeedParts from '@/components/RaceVideoPage/RaceVideoDataViewPane/parts/TelemetryCarSpeedParts.vue'
import TelemetryRpmParts from '@/components/RaceVideoPage/RaceVideoDataViewPane/parts/TelemetryRpmParts.vue'
import TelemetrySteeringAngleParts from '@/components/RaceVideoPage/RaceVideoDataViewPane/parts/TelemetrySteeringAngleParts.vue'
import TelemetryBrkPressFrParts from '@/components/RaceVideoPage/RaceVideoDataViewPane/parts/TelemetryBrkPressFrParts.vue'
import TelemetryRpmGaugeParts from '@/components/RaceVideoPage/RaceVideoDataViewPane/parts/TelemetryRpmGaugeParts.vue'
import TelemetryThrottlePedalParts from '@/components/RaceVideoPage/RaceVideoDataViewPane/parts/TelemetryThrottlePedalParts.vue'
import TelemetryFuelUsedParts from '@/components/RaceVideoPage/RaceVideoDataViewPane/parts/TelemetryFuelUsedParts.vue'
import TelemetryOtsParts from '@/components/RaceVideoPage/RaceVideoDataViewPane/parts/TelemetryOtsParts.vue'
import TelemetryTireTemperatureSection from '@/components/RaceVideoPage/RaceVideoDataViewPane/TelemetryTireTemperatureSection.vue'
import { TelemetryDataType } from '@/store/stores/collectionModule/documents/telemetry/TelemetryDocument'
import { LockOrientationType } from '@/views/RaceVideoPage/RaceVideoPage.vue'

export type DataType = {
  diffMs: number
}

/**
 * レース動画再生画面 テレメトリーセクションのコンポーネント
 */
export default defineComponent({
  name: 'RaceTelemetrySection',
  components: {
    TelemetryGearPosParts,
    TelemetryCarSpeedParts,
    TelemetryRpmParts,
    TelemetrySteeringAngleParts,
    TelemetryBrkPressFrParts,
    TelemetryRpmGaugeParts,
    TelemetryThrottlePedalParts,
    TelemetryFuelUsedParts,
    TelemetryOtsParts,
    TelemetryTireTemperatureSection,
  },
  props: {
    /**
     * 画面固定
     */
    lockOrientation: {
      type: String as PropType<LockOrientationType>,
    },
    /**
     * 画面向き
     */
    screenOrientationType: {
      type: String,
      default: 'portrait-primary',
    },
    /**
     * 表示対象となるテレメトリーデータ
     */
    telemetryData: {
      type: Object as PropType<TelemetryDataType>,
      require: true,
    },
  },
  data(): DataType {
    return {
      diffMs: 0, // 数値表示 実データ間の穴埋め差分用mz
    }
  },
  setup(props) {
    const { adjustPosition } = useTelemetrySectionUtil()

    // 位置指定を整数化
    if (props.lockOrientation === undefined) {
      adjustPosition('.race-telemetry-section')
    }

    const currentTelemetryData = computed(() => props.telemetryData)
    return {
      currentTelemetryData,
    }
  },
  mounted() {
    /**
     * 数値表示 実データ間の穴埋め差分用ms計算
     */
    const socketInterval = 10 // 実データが秒間 XX 回来るか (※変更の際はゲージのアニメーション速度も要修正)
    const ms = Math.floor(1000 / socketInterval) // トデータを差し替えるms計算
    const fpsMs = Math.floor(1000 / 60) // 表示FPSのms計算 ※描画速度を切り替える場合ここを修正
    this.diffMs = Math.ceil(ms / fpsMs) // 数値表示 実データ間の穴埋め差分用ms計算
  },
  methods: {},
})
</script>

<style lang="scss" scoped>
.race-telemetry-section {
  --telemetory-height: calc(var(--screen-height) - 56.25vw - 44px);
  --telemetory-font-size: min(3.2vw, calc(var(--telemetory-height) * 0.0217));

  font-size: var(--telemetory-font-size);
  position: relative;
  width: min(100%, 31.25em);
  padding: min(4.2667vw, 1em);
  margin: 0 auto;
  font-feature-settings: unset;
  overflow: hidden;

  &__bg {
    width: 100%;
  }

  &--landscape-primary,
  &--landscape-secondary {
    --telemetory-height: min(#{var(--screen-height)}, #{calc(100vh - 44px)});
    --telemetory-font-size: min(#{calc(#{var(--telemetory-height)} * 0.02251407)}, 12px);

    font-size: min(#{var(--telemetory-font-size)}, 7.5px);
    position: relative;
    width: min(31.25em, 230px);
    height: calc(#{var(--screen-height)} - 44px);
    padding: 0.6em 0.4em 0;

    .race-telemetry-section__bg {
      display: block;
      width: 100%;
      margin: 0 auto;
    }
  }
}
</style>
